import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';
import Marquee from 'react-fast-marquee';
import axios from 'axios';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Fetch featured products
        const response = await axios.get(`${API}/products?featured=true`);
        setFeaturedProducts(response.data.products || response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const categories = [
    { 
      name: 'T-SHIRTS', 
      path: '/shop/men',
      image: 'https://customer-assets.emergentagent.com/job_threads-store-109/artifacts/ra38wvic_A4A67DC2-65C8-4930-8505-AA96BE9E78EF.png'
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[90vh] overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-top"
          style={{ 
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_threads-store-109/artifacts/uq9h5ui0_FC2A5207-B101-480A-BFA8-C52B41CF486B.png)'
          }}
        >
          <div className="absolute inset-0 bg-black/50" />
        </div>
        
        <div className="relative h-full flex flex-col justify-end px-6 md:px-12 lg:px-24 pb-24">
          <h1 
            className="text-5xl sm:text-7xl lg:text-8xl uppercase tracking-tighter font-bold leading-none mb-6"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            1209<br/>
            <span className="text-primary">APPAREL</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mb-8">
            HIGH QUALITY. SUPER COMFORTABLE. 
            LIMITED EDITION STREETWEAR.
          </p>
          <div className="flex gap-4">
            <Link to="/shop/men">
              <Button 
                className="bg-white text-black hover:bg-primary hover:text-white transition-colors h-12 px-8 text-sm font-bold uppercase tracking-wide"
                data-testid="shop-now-btn"
              >
                SHOP NOW
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="px-6 md:px-12 lg:px-24 py-16" data-testid="categories-section">
        <h2 
          className="text-4xl sm:text-5xl uppercase tracking-tight font-bold mb-12"
          style={{ fontFamily: "'Oswald', sans-serif" }}
        >
          SHOP BY STYLE
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
          {categories.map((category) => (
            <Link 
              key={category.name}
              to={category.path}
              className="group relative aspect-[3/4] overflow-hidden border border-border"
              data-testid={`category-${category.name.toLowerCase()}`}
            >
              <img 
                src={category.image}
                alt={category.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors" />
              <div className="absolute inset-0 flex items-center justify-center">
                <h3 
                  className="text-4xl md:text-5xl font-bold uppercase tracking-tight group-hover:text-primary transition-colors"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  {category.name}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="px-6 md:px-12 lg:px-24 py-16 border-t border-border" data-testid="featured-section">
        <div className="flex justify-between items-end mb-12">
          <h2 
            className="text-4xl sm:text-5xl uppercase tracking-tight font-bold"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            FEATURED
          </h2>
          <Link 
            to="/shop/men"
            className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground hover:text-primary transition-colors hidden md:flex items-center gap-2"
            data-testid="view-all-link"
          >
            VIEW ALL
            <ArrowRight size={14} />
          </Link>
        </div>
        
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
            {[...Array(4)].map((_, i) => (
              <div key={`skeleton-${i}`} className="border border-border">
                <div className="aspect-[3/4] bg-muted animate-pulse" />
                <div className="p-4 border-t border-border space-y-2">
                  <div className="h-4 bg-muted animate-pulse w-3/4" />
                  <div className="h-3 bg-muted animate-pulse w-1/2" />
                  <div className="h-4 bg-muted animate-pulse w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
            {featuredProducts.slice(0, 4).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

        <div className="mt-8 md:hidden">
          <Link to="/shop/men">
            <Button 
              variant="outline"
              className="w-full border-border hover:bg-primary hover:text-white hover:border-primary transition-colors"
              data-testid="view-all-btn-mobile"
            >
              VIEW ALL PRODUCTS
            </Button>
          </Link>
        </div>
      </section>

      {/* Book Promo Section */}
      <section className="px-6 md:px-12 lg:px-24 py-16 border-t border-border" data-testid="book-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center max-w-5xl">
          <div className="flex justify-center md:justify-start">
            <div className="w-48 sm:w-56 aspect-[2/3] border border-border overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_threads-store-109/artifacts/26nq4udt_6430819E-94EA-4DC6-9264-16266FE523B8.png"
                alt="The Weight Of The Helmet"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-3">
              NEW RELEASE
            </p>
            <h2
              className="text-4xl sm:text-5xl uppercase tracking-tighter font-bold mb-4"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              THE WEIGHT OF THE HELMET
            </h2>
            <p className="text-sm uppercase tracking-wide text-muted-foreground mb-2">
              BY QUINTON WASHINGTON
            </p>
            <p className="text-muted-foreground mb-6 max-w-md">
              A story of brotherhood, resilience, and letting go. $15 with free shipping.
            </p>
            <Link to="/book">
              <Button
                className="bg-white text-black hover:bg-primary hover:text-white transition-colors h-12 px-8 text-sm font-bold uppercase tracking-wide"
                data-testid="buy-book-btn"
              >
                BUY BOOK
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-primary text-black px-6 md:px-12 lg:px-24 py-16" data-testid="cta-section">
        <div className="max-w-4xl">
          <h2 
            className="text-4xl sm:text-6xl uppercase tracking-tight font-bold mb-6"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            JOIN THE<br/>COLLECTIVE
          </h2>
          <p className="text-lg mb-8 max-w-xl">
            SUBSCRIBE FOR EXCLUSIVE DROPS, EARLY ACCESS, AND 10% OFF YOUR FIRST ORDER.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md">
            <input 
              type="email"
              placeholder="ENTER YOUR EMAIL"
              className="flex-1 bg-transparent border-2 border-black px-4 py-3 text-black placeholder:text-black/60 focus:outline-none focus:ring-2 focus:ring-black"
              data-testid="email-input"
            />
            <Button 
              className="bg-black text-white hover:bg-white hover:text-black transition-colors px-8 py-3 h-auto"
              data-testid="subscribe-btn"
            >
              SUBSCRIBE
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
